/**
 * Non-working-day awareness for the time-log forms.
 *
 * Logging time on a Sunday or a public holiday is usually a mistyped date — but
 * not always (weekend releases, on-call, holiday cover), so this warns and then
 * gets out of the way. Nothing here blocks a save.
 *
 * The calendar is fetched ONCE for a wide window and every check after that is
 * local, so the warning appears the instant the date changes rather than after a
 * round trip per keystroke. The backend answers from services/workingDays — the
 * same module the missing-time-log reminder uses — so a day this calls a week-off
 * is never a day the reminder later chases as missing.
 */
import { useEffect, useState, useCallback } from "react";
import { wsApi } from "./wsApi";

const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

// Weekday index of a 'YYYY-MM-DD' string, read at UTC midnight so no local
// offset can shift the day. Mirrors the backend's weekdayOf().
const weekdayOf = (isoDay) => new Date(`${isoDay}T00:00:00Z`).getUTCDay();

const isIsoDay = (v) => typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v);

export default function useNonWorkingDays() {
  // null until loaded. A failed fetch leaves it null and every check returns no
  // warning — a calendar we couldn't read must not invent week-offs.
  const [calendar, setCalendar] = useState(null);

  useEffect(() => {
    let alive = true;
    wsApi
      .nonWorkingDays()
      .then((d) => {
        if (alive) setCalendar(d);
      })
      .catch(() => {
        /* warning is a nicety — logging time must still work without it */
      });
    return () => {
      alive = false;
    };
  }, []);

  /**
   * Why `isoDay` is not a working day, or null when it is (or when unknown).
   * @returns {{kind:'holiday'|'weekoff', reason:string, day:string}|null}
   */
  const checkDay = useCallback(
    (isoDay) => {
      if (!calendar || !isIsoDay(isoDay)) return null;
      const holiday = calendar.holidays?.[isoDay];
      if (holiday) return { kind: "holiday", reason: holiday, day: isoDay };
      const dow = weekdayOf(isoDay);
      const working = calendar.working_weekdays || [];
      // Only trust an explicit working-day list; an empty one means we don't
      // know, and guessing Mon–Fri would warn people on legitimate schedules.
      if (working.length && !working.includes(dow))
        return { kind: "weekoff", reason: DAY_NAMES[dow], day: isoDay };
      return null;
    },
    [calendar],
  );

  return { checkDay, calendar };
}

// One-line human form of a checkDay() result, shared by the inline hint and the
// confirm dialog so they always word it the same way.
export const nonWorkingLabel = (w) =>
  !w
    ? ""
    : w.kind === "holiday"
      ? `${w.reason} — a company holiday`
      : `a ${w.reason} — outside your working days`;
